import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

/* ─── Constants ─────────────────────────────────────── */
const TOOLTIP_GAP = 12;
const TOOLTIP_FLIP_THRESHOLD = 88;

/* ─── Shared tooltip helpers ────────────────────────── */
const positionTooltip = (wrap, event) => {
  const rect = wrap.getBoundingClientRect();
  const clientX = event?.clientX ?? 0;
  const clientY = event?.clientY ?? 0;
  const placement = clientY - rect.top < TOOLTIP_FLIP_THRESHOLD ? "below" : "above";
  return { x: clientX, y: clientY, placement };
};

const ChartTooltip = ({ tooltip }) => {
  if (!tooltip) return null;
  const below = tooltip.placement === "below";
  return (
    <div
      className="ev-chart-tooltip"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        transform: below
          ? `translate(-50%, ${TOOLTIP_GAP}px)`
          : `translate(-50%, calc(-100% - ${TOOLTIP_GAP}px))`,
      }}
      role="tooltip"
    >
      {tooltip.title && <div className="ev-chart-tooltip-title">{tooltip.title}</div>}
      {tooltip.subtitle && <div className="ev-chart-tooltip-sub">{tooltip.subtitle}</div>}
      <div className="ev-chart-tooltip-value">{tooltip.value}</div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   1. RADAR CHART  (Metric Overview)
══════════════════════════════════════════════════════ */
export const RadarMetricChart = ({ data, overallScore = "0.81" }) => {
  /**
   * data: [{ axis: string, value: 0–1 }]
   */
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !data?.length) return;

    /* ── layout: fixed viewBox with generous padding so labels never clip ── */
    const W = 340;
    const H = 340;
    const cx = W / 2;
    const cy = H / 2;
    /* radar polygon radius — leave 70px each side for labels + value text */
    const radius = 100;
    const levels = 5;
    const total = data.length;
    const angleSlice = (Math.PI * 2) / total;

    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${W} ${H}`).attr("preserveAspectRatio", "xMidYMid meet");
    svg.selectAll("*").remove();

    const rScale = d3.scaleLinear().domain([0, 1]).range([0, radius]);

    /* ── grid rings (hexagonal) ── */
    for (let lvl = 1; lvl <= levels; lvl++) {
      const r = (radius / levels) * lvl;
      const pts = data.map((_, i) => {
        const angle = angleSlice * i - Math.PI / 2;
        return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
      });
      svg.append("polygon")
        .attr("points", pts.map((p) => p.join(",")).join(" "))
        .attr("fill", "none")
        .attr("stroke", "#d1d5db")
        .attr("stroke-width", 1);
    }

      /* ── axis lines ── */
      data.forEach((_, i) => {
          const angle = angleSlice * i - Math.PI / 2;
          svg.append("line")
              .attr("x1", cx).attr("y1", cy)
              .attr("x2", cx + radius * Math.cos(angle))
              .attr("y2", cy + radius * Math.sin(angle))
              .attr("stroke", "#d1d5db")
              .attr("stroke-width", 1);
      });

      /* ── axis labels — pushed further out, text-anchor logic per quadrant ── */
      /* ── axis labels + value, stacked together under each vertex ── */
      data.forEach((d, i) => {
          const angle = angleSlice * i - Math.PI / 2;
          const labelR = radius + 34;
          const lx = cx + labelR * Math.cos(angle);
          const ly = cy + labelR * Math.sin(angle);

          const cos = Math.cos(angle);
          const anchor = cos < -0.3 ? "end" : cos > 0.3 ? "start" : "middle";

          /* split multi-word labels onto two lines */
          const words = d.axis.split(" ");
          let lines;
          if (words.length > 1) {
              const mid = Math.ceil(words.length / 2);
              lines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
          } else {
              lines = [d.axis];
          }

          const labelText = svg.append("text")
              .attr("text-anchor", anchor)
              .attr("fill", "#64748b")
              .attr("font-size", 9.5)
              .attr("font-weight", 500);

          /* label line(s), vertically centered around ly */
          const startOffset = -((lines.length - 1) * 5.5);
          lines.forEach((line, li) => {
              labelText.append("tspan")
                  .attr("x", lx)
                  .attr("y", ly + startOffset + li * 11)
                  .text(line);
          });

          /* value, directly below the label line(s) */
          labelText.append("tspan")
              .attr("x", lx)
              .attr("y", ly + startOffset + lines.length * 11)
              .attr("fill", "#374151")
              .attr("font-weight", 700)
              .attr("font-size", 9.5)
              .text(d.value.toFixed(2));
      });

      /* ── filled polygon ── */
      const polyPts = data.map((d, i) => {
          const angle = angleSlice * i - Math.PI / 2;
          const r = rScale(d.value);
          return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
      });

      svg.append("polygon")
          .attr("points", polyPts.map((p) => p.join(",")).join(" "))
          .attr("fill", "#4f46e5")
          .attr("fill-opacity", 0.18)
          .attr("stroke", "#4f46e5")
          .attr("stroke-width", 2);

      /* ── dots + tooltips only (no separate value text anymore) ── */
      data.forEach((d, i) => {
          const angle = angleSlice * i - Math.PI / 2;
          const r = rScale(d.value);
          const px = cx + r * Math.cos(angle);
          const py = cy + r * Math.sin(angle);

          svg.append("circle")
              .attr("cx", px).attr("cy", py)
              .attr("r", 4.5)
              .attr("fill", "#fff")
              .attr("stroke", "#4f46e5")
              .attr("stroke-width", 2)
              .style("cursor", "pointer")
              .on("mouseenter", (event) => {
                  const pos = positionTooltip(wrap, event);
                  setTooltip({ ...pos, title: d.axis, value: d.value.toFixed(2) });
              })
              .on("mousemove", (event) => {
                  const pos = positionTooltip(wrap, event);
                  setTooltip((prev) => prev && { ...prev, ...pos });
              })
              .on("mouseleave", () => setTooltip(null));
      });

      /* ── center score label ── */
      svg.append("text")
          .attr("x", cx).attr("y", cy - 9)
          .attr("text-anchor", "middle")
          .attr("fill", "#0f172a")
          .attr("font-size", 22)
          .attr("font-weight", 800)
          .text(overallScore);

      svg.append("text")
          .attr("x", cx).attr("y", cy + 13)
          .attr("text-anchor", "middle")
          .attr("fill", "#94a3b8")
          .attr("font-size", 9)
          .attr("font-weight", 500)
          .text("Overall Score");
  }, [data, overallScore]);

    if (!data?.length) return <div className="ev-chart-empty">No metric data</div>;

    return (
        <div ref={wrapRef} className="ev-radar-wrap">
            <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
            <ChartTooltip tooltip={tooltip} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════
   2. MULTI-LINE CHART  (Executions Over Time)
══════════════════════════════════════════════════════ */
const LINE_COLORS = { Total: "#2563eb", Success: "#22c55e", Failed: "#ef4444" };
const MARGIN = { top: 12  , right: 14, bottom: 36, left: 44 };
const CHART_HEIGHT = 220;

const nearestPoint = (rows, xScale, pointerX) => {
  if (!rows?.length) return null;
  let nearest = rows[0];
  let minDist = Math.abs(xScale(rows[0].date) - pointerX);
  rows.forEach((d) => {
    const dist = Math.abs(xScale(d.date) - pointerX);
    if (dist < minDist) { minDist = dist; nearest = d; }
  });
  return nearest;
};

/* Legend rendered as HTML (not inside the SVG) so it can sit flush under
   the card title regardless of how the chart itself is vertically centered. */
const LineChartLegend = () => (
  <div className="ev-chart-legend">
    {Object.entries(LINE_COLORS).map(([key, color]) => (
      <div className="ev-chart-legend-item" key={key}>
        <span className="ev-chart-legend-dot" style={{ background: color }} />
        <span>{key}</span>
      </div>
    ))}
  </div>
);

export const ExecutionsLineChart = ({ data }) => {
    /**
     * data: [{ date: string, Total: number, Success: number, Failed: number }]
     */
    const wrapRef = useRef(null);
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    useEffect(() => {
        setTooltip(null);
        const wrap = wrapRef.current;
        const svgEl = svgRef.current;
        if (!wrap || !svgEl || !data?.length) return;

        const width = Math.max(wrap.clientWidth || 400, 280);
        // const height = CHART_HEIGHT;
        const height = Math.min(Math.max(wrap.clientHeight || 220, 220),260);
        const m = MARGIN;

        const svg = d3.select(svgEl);
        svg.attr("viewBox", `0 0 ${width} ${height}`);
        svg.selectAll("*").remove();

        const keys = ["Total", "Success", "Failed"];
        const allVals = data.flatMap((d) => keys.map((k) => d[k] || 0));
        const maxY = Math.max(d3.max(allVals) || 0, 1);

        const x = d3.scalePoint()
            .domain(data.map((d) => d.date))
            .range([m.left, width - m.right])
            .padding(0.4);

        const y = d3.scaleLinear()
            .domain([0, maxY]).nice()
            .range([height - m.bottom, m.top]);

        

        /* grid */
        svg.append("g")
            .selectAll("line")
            .data(y.ticks(4))
            .join("line")
            .attr("x1", m.left).attr("x2", width - m.right)
            .attr("y1", (v) => y(v)).attr("y2", (v) => y(v))
            .attr("stroke", "#f1f5f9").attr("stroke-width", 1);

        /* y-axis */
        svg.append("g")
            .attr("transform", `translate(${m.left},0)`)
            .call(d3.axisLeft(y).ticks(4).tickSizeOuter(0))
            .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", 10))
            .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

        /* x-axis */
        const step = Math.max(1, Math.ceil(data.length / 7));
        const ticks = data.filter((_, i) => i % step === 0).map((d) => d.date);
        svg.append("g")
            .attr("transform", `translate(0,${height - m.bottom})`)
            .call(d3.axisBottom(x).tickValues(ticks))
            .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", 9))
            .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

        /* lines + areas */
        keys.forEach((key) => {
            const lineGen = d3.line()
                .x((d) => x(d.date))
                .y((d) => y(d[key] || 0))
                .curve(d3.curveMonotoneX);

            const col = LINE_COLORS[key];
            const isTotal = key === "Total";

            svg.append("path").datum(data)
                .attr("fill", "none")
                .attr("stroke", col)
                .attr("stroke-width", isTotal ? 5 : 2)
                .attr("stroke-linecap", "round")
                .attr("d", lineGen);

            svg.selectAll(`circle.dot-${key}`)
                .data(data)
                .join("circle")
                .attr("class", `dot-${key}`)
                .attr("cx", (d) => x(d.date))
                .attr("cy", (d) => y(d[key] || 0))
                .attr("r", isTotal ? 5 : 3)
                .attr("fill", "#fff")
                .attr("stroke", col)
                .attr("stroke-width", 2);
        });
        /* focus line */
        const focus = svg.append("g").style("display", "none");
        focus.append("line")
            .attr("y1", m.top).attr("y2", height - m.bottom)
            .attr("stroke", "#94a3b8").attr("stroke-width", 1)
            .attr("stroke-dasharray", "4,3");

        /* overlay */
        svg.append("rect")
            .attr("x", m.left).attr("y", m.top)
            .attr("width", width - m.left - m.right)
            .attr("height", height - m.top - m.bottom)
            .attr("fill", "transparent")
            .style("cursor", "crosshair")
            .on("mousemove", (event) => {
                const [mx] = d3.pointer(event);
                const d = nearestPoint(data, x, mx);
                if (!d) return;
                const cx = x(d.date);
                focus.style("display", null);
                focus.select("line").attr("x1", cx).attr("x2", cx);
                const pos = positionTooltip(wrap, event);
                setTooltip({
                    ...pos,
                    title: d.date,
                    value: `Total: ${d.Total}  ✓ ${d.Success}  ✗ ${d.Failed}`,
                });
            })
            .on("mouseleave", () => { focus.style("display", "none"); setTooltip(null); });

        focus.raise();
    }, [data]);

    if (!data?.length) return <div className="ev-chart-empty">No execution data</div>;

    return (
        <div className="ev-line-chart-wrap">
            <LineChartLegend />
            <div ref={wrapRef} className="ev-chart-surface">
                <svg ref={svgRef} style={{ width: "100%", height: "100%", display: "block" }} />
                <ChartTooltip tooltip={tooltip} />
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════
   3. DONUT CHART  (Failure Distribution)
══════════════════════════════════════════════════════ */
export const FailureDonutChart = ({ data, totalCount }) => {
  /**
   * data: [{ name: string, value: number, color: string }]  -- value is a %
   * totalCount: actual total failure count to display in center
   */
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const percentSum = data?.reduce((s, d) => s + d.value, 0) || 0;
  const displayTotal = totalCount ?? percentSum;

  useEffect(() => {
    setTooltip(null);
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !data?.length || percentSum === 0) return; // <-- fixed

    const size = Math.min(wrap.clientHeight || 180, 220); 
    const radius = size / 2 - 8;

    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${size} ${size}`);
    svg.selectAll("*").remove();

    const g = svg.append("g").attr("transform", `translate(${size / 2},${size / 2})`);
    const pie = d3.pie().value((d) => d.value).sort(null).padAngle(0.025);
    const arc = d3.arc().innerRadius(radius * 0.56).outerRadius(radius).cornerRadius(4);
    const arcHover = d3.arc().innerRadius(radius * 0.52).outerRadius(radius + 4).cornerRadius(4);

    g.selectAll("path")
      .data(pie(data))
      .join("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2.5)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("d", arcHover);
        const pos = positionTooltip(wrap, event);
        setTooltip({ ...pos, title: d.data.name, value: `${d.data.value}%` });
      })
      .on("mousemove", (event, d) => {
        const pos = positionTooltip(wrap, event);
        setTooltip((prev) => prev && { ...prev, ...pos, title: d.data.name, value: `${d.data.value}%` });
      })
      .on("mouseleave", function () {
        d3.select(this).attr("d", arc);
        setTooltip(null);
      });

    /* center label */
    g.append("text")
      .attr("text-anchor", "middle").attr("dy", "-0.2em")
      .attr("fill", "#0f172a").attr("font-size", 20).attr("font-weight", 800)
      .text(displayTotal.toLocaleString()); // <-- fixed

    g.append("text")
      .attr("text-anchor", "middle").attr("dy", "1.3em")
      .attr("fill", "#94a3b8").attr("font-size", 9).attr("font-weight", 500)
      .text("Total Failures");
  }, [data, percentSum, displayTotal]); // <-- fixed deps

  if (!data?.length) return <div className="ev-chart-empty">No failure data</div>;

  return (
    <div ref={wrapRef} className="ev-donut-wrap">
      <div style={{ position: "relative", flexShrink: 0 }}>
        <svg ref={svgRef} style={{ width: 180, height: 180, display: "block" }} />
        <ChartTooltip tooltip={tooltip} />
      </div>
      <div className="ev-donut-legend">
        {data.map((d, i) => (
          <div className="ev-donut-legend-row" key={i}>
            <span className="ev-donut-swatch" style={{ background: d.color }} />
            <span className="ev-donut-label">{d.name}</span>
            <span className="ev-donut-pct">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   4. VERTICAL BAR CHART  (Score Distribution)
══════════════════════════════════════════════════════ */
const BAR_MARGIN = { top: 24, right: 10, bottom: 40, left: 44 };

export const ScoreBarChart = ({ data }) => {
  /**
   * data: [{ range: string, count: number, color: string }]
   */
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    setTooltip(null);
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !data?.length) return;

    const width = Math.max(wrap.clientWidth || 300, 220);
    const height = 220;
    const m = BAR_MARGIN;

    const svg = d3.select(svgEl);
    svg.attr("viewBox", `0 0 ${width} ${height}`);
    svg.selectAll("*").remove();

    const maxY = Math.max(d3.max(data, (d) => d.count) || 0, 1);

    const x = d3.scaleBand()
      .domain(data.map((d) => d.range))
      .range([m.left, width - m.right])
      .padding(0.28);

    const y = d3.scaleLinear()
      .domain([0, maxY]).nice()
      .range([height - m.bottom, m.top]);

    /* grid */
    svg.append("g")
      .selectAll("line")
      .data(y.ticks(4))
      .join("line")
      .attr("x1", m.left).attr("x2", width - m.right)
      .attr("y1", (v) => y(v)).attr("y2", (v) => y(v))
      .attr("stroke", "#f1f5f9").attr("stroke-width", 1);

    /* y-axis */
    svg.append("g")
      .attr("transform", `translate(${m.left},0)`)
      .call(d3.axisLeft(y).ticks(4).tickFormat(d3.format(",")).tickSizeOuter(0))
      .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", 10))
      .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

    /* x-axis */
    svg.append("g")
      .attr("transform", `translate(0,${height - m.bottom})`)
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .call((g) => g.selectAll("text").attr("fill", "#94a3b8").attr("font-size", 9))
      .call((g) => g.selectAll("path,line").attr("stroke", "rgba(15,23,42,0.06)"));

    /* x-axis label */
    svg.append("text")
      .attr("x", (m.left + width - m.right) / 2)
      .attr("y", height - 2)
      .attr("text-anchor", "middle")
      .attr("fill", "#94a3b8")
      .attr("font-size", 9)
      .text("Evaluation Score");

    /* bars */
    svg.selectAll("rect.bar")
      .data(data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.range))
      .attr("y", (d) => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", (d) => height - m.bottom - y(d.count))
      .attr("rx", 4)
      .attr("fill", (d) => d.color)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("opacity", 0.82);
        const pos = positionTooltip(wrap, event);
        setTooltip({ ...pos, title: d.range, subtitle: "Evaluation Score range", value: d.count.toLocaleString() });
      })
      .on("mousemove", (event, d) => {
        const pos = positionTooltip(wrap, event);
        setTooltip((prev) => prev && { ...prev, ...pos });
      })
      .on("mouseleave", function () {
        d3.select(this).attr("opacity", 1);
        setTooltip(null);
      });

    /* value labels on top of bars */
    svg.selectAll("text.val")
      .data(data)
      .join("text")
      .attr("class", "val")
      .attr("x", (d) => x(d.range) + x.bandwidth() / 2)
      .attr("y", (d) => y(d.count) - 4)
      .attr("text-anchor", "middle")
      .attr("fill", "#64748b")
      .attr("font-size", 9)
      .attr("font-weight", 600)
      .style("pointer-events", "none")
      .text((d) => d3.format(",")(d.count));
  }, [data]);

  if (!data?.length) return <div className="ev-chart-empty">No score data</div>;

  return (
    <div ref={wrapRef} className="ev-chart-surface">
      <svg ref={svgRef} style={{ width: "100%", height: 220, display: "block" }} />
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
};